import { FieldValues, UseFormRegister } from 'react-hook-form';
import { Box, TimePicker, TimePickerOption, Typography } from '@vp/swan';
import { BoxedLoader, bugtracker } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { usePage } from '@99designs/tracking';
import { useDesignLiveAvailableTimesQuery } from './appointments.generated';

interface TimeSelectorProps {
    timezone: string;
    date: string;
    setTime: (time: string) => void;
    time: string;
    defaultValue: string;
    immutable: boolean | null;
    register: UseFormRegister<FieldValues>;
    id: string;
}

export default function TimeSelector({
    timezone,
    setTime,
    date,
    time,
    defaultValue,
    immutable,
    register,
    id,
}: TimeSelectorProps) {
    const disabled = !!immutable && defaultValue !== '';
    const { locale } = usePage();
    const { data, loading, error } = useDesignLiveAvailableTimesQuery({
        variables: {
            date: date,
            timezone: timezone,
            locale: locale,
        },
        onCompleted: (d) => {
            if (!defaultValue) return;

            const times = d.setField.times;

            const defaultTimeInAvailableTimes = times.find((d) => d.startTimeUTC === defaultValue);

            if (defaultTimeInAvailableTimes) {
                setTime(defaultValue);
            }
        },
        onError: (e) => {
            bugtracker.notify(`Failed to get available design live times: ${e.message}`);
        },
    });

    if (error) {
        return (
            <Box marginTop="5">
                <Typography fontSize="small" textColor="error">
                    {__(
                        'There was an error fetching available times. Please refresh the page to try again.',
                        {
                            _context:
                                'Message shown to customers scheduling a call when there is an error fetching available times.',
                        }
                    )}
                </Typography>
            </Box>
        );
    }

    if (loading) {
        return <BoxedLoader label={__('Loading available times...')} />;
    }

    if (data?.setField?.times.length === 0) {
        return (
            <Typography fontSize="small">
                {__(`There are no available times. Please select another date.`)}
            </Typography>
        );
    }

    return (
        <TimePicker
            value={time}
            {...register(id, {
                required: __('Please select a date and time', {
                    _context: 'Validation error shown to customers scheduling a call.',
                }),
            })}
            onChange={(e) => setTime(e.target.value)}
            timeZone={timezone}
            id={id}
            style={{ width: '100%' }}
            disabled={disabled}
        >
            <TimePickerOption
                placeholderText={__('Pick your session time', {
                    _context: 'Placeholder text in a time picker drop down',
                })}
                value={''}
            />
            {data?.setField?.times.map((t) => (
                <TimePickerOption
                    value={t.startTimeUTC}
                    startTime={new Date(t.startTimeUTC)}
                    endTime={new Date(t.endTimeUTC)}
                />
            ))}
        </TimePicker>
    );
}
